import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowRight, BookOpen, CheckCircle2, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";

/**
 * "Worry Less" free chapter email opt-in.
 * Used across all city-specific specialty landing pages.
 */
export const WorryLessChapterOptIn = () => {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/lead-magnet/worry-less-chapter`, {
        first_name: firstName.trim() || undefined,
        email: trimmedEmail,
        page_path:
          typeof window !== "undefined" ? window.location.pathname : undefined,
      });
      setDone(true);
      toast.success("Check your inbox — your free chapter is on its way!");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(
        typeof msg === "string" ? msg : "Something went wrong. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      className="py-20 md:py-24 px-6 md:px-12 bg-secondary/30"
      data-testid="lead-magnet-section"
    >
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative bg-white rounded-3xl shadow-soft p-8 md:p-12 overflow-hidden"
        >
          <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-primary/5 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-16 -left-16 h-48 w-48 rounded-full bg-accent/10 blur-3xl pointer-events-none" />

          <div className="relative grid grid-cols-1 md:grid-cols-5 gap-8 items-center">
            <div className="md:col-span-3">
              <span className="inline-flex items-center gap-2 text-primary text-xs font-semibold uppercase tracking-[0.18em]">
                <BookOpen className="h-4 w-4" />
                Free Gift
              </span>
              <h2 className="font-serif text-3xl md:text-4xl font-semibold text-foreground mt-3 leading-tight">
                Get a free chapter of{" "}
                <span className="italic text-primary">
                  &ldquo;Worry Less &amp; Beat That Anxiety!&rdquo;
                </span>
              </h2>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                A gentle, grounding excerpt from Laura&rsquo;s book — practical tools
                to quiet racing thoughts, soften the inner critic, and start feeling
                like yourself again. Delivered to your inbox in seconds.
              </p>

              {done ? (
                <div
                  className="mt-6 flex items-start gap-3 rounded-2xl bg-green-50 border border-green-200 p-4"
                  data-testid="lead-magnet-success"
                >
                  <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-green-900">Check your inbox!</p>
                    <p className="text-sm text-green-800 mt-0.5">
                      Your free chapter is on its way. If you don&rsquo;t see it within
                      a few minutes, please check your spam folder.
                    </p>
                  </div>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="mt-6 space-y-3"
                  data-testid="lead-magnet-form"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Input
                      type="text"
                      placeholder="First name (optional)"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      maxLength={80}
                      className="rounded-full px-5 py-6 bg-white border-border"
                      data-testid="lead-magnet-firstname-input"
                    />
                    <Input
                      type="email"
                      placeholder="Your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="rounded-full px-5 py-6 bg-white border-border"
                      data-testid="lead-magnet-email-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="rounded-full w-full sm:w-auto px-8 py-6 bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid="lead-magnet-submit-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Me the Free Chapter
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-muted-foreground pt-1">
                    No spam, ever. Your email is confidential and protected under
                    HIPAA-aligned practices.
                  </p>
                </form>
              )}
            </div>

            <div className="md:col-span-2 flex justify-center">
              <img
                src="https://customer-assets.emergentagent.com/job_therapy-solutions-1/artifacts/unwyel7v_IMG_2280.jpeg"
                alt="Worry Less &amp; Beat That Anxiety! book cover by Laura A. Jeanis"
                className="w-44 md:w-56 rounded-2xl shadow-hover"
                data-testid="lead-magnet-book-cover"
              />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WorryLessChapterOptIn;
